<template>
  <div
    class="setup-task-detail-page"
    data-testid="setup-task-detail"
  >
    <v-card
      data-testid="setup-task-detail__main-card"
      class="all-width flex-card shadow-off"
      :loading="activeTemplateLoading"
      loader-height="2"
    >
      <v-card-text class="page-table-layout__body gap-2">
        <div v-if="activeTemplateLoading && !activeTemplate" data-testid="setup-task-detail__skeleton-loader">
          <v-skeleton-loader type="heading" class="mb-4" />
          <v-skeleton-loader type="paragraph" class="mb-6" />
          <v-skeleton-loader type="table-row@6" />
        </div>

        <template v-else-if="activeTemplate">
          <TaskDetailPageHeader
            :template="activeTemplate"
            :loading="activeTemplateLoading"
            @back="goBack"
            @edit="handleEditTemplate"
          />

          <TaskDetailBasics
            :template="activeTemplate"
            :custom-fields="customColumnsForCycleType"
            class="setup-task-detail-page__basics"
          />

          <div class="setup-task-detail-page__tabs-row">
            <FcTabSwitcher
              :tabs="detailTabOptions"
              :value="detailTab"
              variant="underline"
              aria-label="Task template detail sections"
              data-testid="setup-task-detail__tabs"
              @input="onDetailTabChange"
            />
          </div>

          <div
            v-if="detailTab === 'assignments'"
            class="setup-task-detail-page__assignments"
            data-testid="setup-task-detail__assignments"
          >
            <v-row dense>
              <v-col
                v-for="(assignment, index) in assignmentGroups"
                :key="`assignment-${assignment.assignment_number}-${index}`"
                cols="12"
                md="6"
              >
                <TaskDetailAssignmentCard
                  :assignment="assignment"
                  :locations="resolvedLocations"
                  :users="resolvedUsers"
                  :show-threshold="isAR"
                />
              </v-col>
            </v-row>

            <p
              v-if="!assignmentGroups.length"
              class="setup-task-detail-page__empty gray--text mb-0"
              data-testid="setup-task-detail__assignments-empty"
            >
              No assignments configured for this template.
            </p>
          </div>

          <TaskDetailAccounts
            v-else-if="detailTab === 'accounts' && isAR"
            :template="activeTemplate"
            :chart-of-accounts="chartOfAccounts"
            data-testid="setup-task-detail__accounts"
          />

          <TaskDetailDependencies
            v-else-if="detailTab === 'dependencies'"
            :blocking="activeTemplate.blocking"
            :blocked-by="activeTemplate.blockedBy"
            :relates="activeTemplate.relates"
            data-testid="setup-task-detail__dependencies-panel"
          />
        </template>

        <div
          v-else
          class="setup-task-detail-page__not-found text-center py-12"
          data-testid="setup-task-detail__not-found"
        >
          <p class="gray--text mb-4">Template not found.</p>
          <v-btn
            outlined
            class="text-none"
            data-testid="setup-task-detail__back-to-list-btn"
            @click="goBack"
          >
            Back to Task Library
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <AddTemplateDialog
      ref="addTemplateDialog"
      :open="addTemplateDialog.open"
      :mode="addTemplateDialog.mode"
      :task-type="addTemplateDialog.taskType"
      :template-id="addTemplateDialog.templateId || templateId"
      :template="activeTemplate"
      :cycle-type="templateCycleType"
      :locations="locations"
      :users="users"
      :chart-of-accounts="chartOfAccounts"
      :form-meta="formMeta"
      :custom-fields="customColumnsForCycleType"
      :loading="templateDialogLoading"
      @save="handleSaveTemplate"
      @cancel="closeAddTaskTemplateDialog"
    />
  </div>
</template>

<script>
import './TaskDetail.scss';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { FcTabSwitcher } from '../../../components';
import AddTemplateDialog from '../TaskList/components/AddTemplateDialog/AddTemplateDialog.vue';
import {
  TaskDetailPageHeader,
  TaskDetailBasics,
  TaskDetailAssignmentCard,
  TaskDetailAccounts,
  TaskDetailDependencies,
} from './components';
import { groupLocationsByAssignmentNumber } from '../TaskList/utils/assignmentGrouping';
import { isAccountReconciliation } from '../TaskList/utils/taskListHelpers';
import { FC_ROUTES } from '../../../utils/constants';
import { readTemplateSaveFieldErrors } from '../TaskList/utils/accessErrorsToFieldErrors';

const DETAIL_TABS = [
  { id: 'assignments', label: 'Assignments' },
  { id: 'accounts', label: 'Accounts' },
  { id: 'dependencies', label: 'Dependencies' },
];

/** Merge bootstrap entities with GET *_details enrichment (details win on conflict). */
function mergeEntitiesById(baseList = [], detailsList = []) {
  const byId = new Map();
  (baseList || []).forEach((item) => {
    const id = item?._id != null ? String(item._id) : item?.id != null ? String(item.id) : '';
    if (!id) return;
    byId.set(id, { ...item, _id: id });
  });
  (detailsList || []).forEach((item) => {
    const id = item?._id != null ? String(item._id) : item?.id != null ? String(item.id) : '';
    if (!id) return;
    byId.set(id, { ...(byId.get(id) || {}), ...item, _id: id });
  });
  return Array.from(byId.values());
}

export default {
  name: 'SetupTaskDetail',

  components: {
    FcTabSwitcher,
    AddTemplateDialog,
    TaskDetailPageHeader,
    TaskDetailBasics,
    TaskDetailAssignmentCard,
    TaskDetailAccounts,
    TaskDetailDependencies,
  },

  data() {
    return {
      templateDialogLoading: false,
    };
  },

  computed: {
    ...mapState('setup', {
      activeTemplate: (s) => s.activeTemplate,
      activeTemplateLoading: (s) => !!s.activeTemplateLoading,
      detailTab: (s) => s.detailTab || 'assignments',
      locations: (s) => s.locations || [],
      users: (s) => s.users || [],
      chartOfAccounts: (s) => s.chartOfAccounts || [],
      customColumns: (s) => s.customColumns || [],
      addTemplateDialog: (s) => s.addTemplateDialog || { open: false },
      formMeta: (s) => s.formMeta || {},
    }),

    ...mapGetters('setup', ['customColumnsForCycleType']),

    templateId() {
      return this.$route.params.id;
    },

    isAR() {
      return isAccountReconciliation(this.activeTemplate?.task_type, this);
    },

    templateCycleType() {
      return (
        this.activeTemplate?.cycle_type ||
        this.activeTemplate?.cycleType ||
        'FBC'
      ).toUpperCase();
    },

    assignmentGroups() {
      if (!this.activeTemplate) return [];
      return groupLocationsByAssignmentNumber(
        this.activeTemplate.location || [],
        this.activeTemplate.task_type
      );
    },

    /** Prefer GET enrichment (location_details) over bootstrap list for name resolution. */
    resolvedLocations() {
      return mergeEntitiesById(
        this.locations,
        this.activeTemplate?.location_details
      );
    },

    /** Prefer GET enrichment (users_details) over bootstrap list for name resolution. */
    resolvedUsers() {
      return mergeEntitiesById(
        this.users,
        this.activeTemplate?.users_details
      );
    },

    detailTabOptions() {
      if (!this.isAR) {
        return DETAIL_TABS.filter((tab) => tab.id !== 'accounts');
      }
      return DETAIL_TABS.map((tab) => {
        if (tab.id === 'assignments') {
          const count = this.assignmentGroups.length;
          return {
            ...tab,
            label: count ? `Assignments` : 'Assignments',
          };
        }
        if (tab.id === 'accounts') {
          const glCount = (this.activeTemplate?.glcode || []).filter(
            (g) => g != null && String(g) !== '' && String(g) !== '[object Object]'
          ).length;
          return {
            ...tab,
            label: glCount ? `Accounts` : 'Accounts',
          };
        }
        return tab;
      });
    },
  },

  watch: {
    '$route.params.id': {
      immediate: false,
      handler(id) {
        if (id) this.loadPage(id);
      },
    },

    isAR(val) {
      if (!val && this.detailTab === 'accounts') {
        this.setDetailTab('assignments');
      }
    },
  },

  created() {
    this.loadPage(this.templateId);
  },

  methods: {
    ...mapActions('setup', [
      'fetchTaskTemplateDetail',
      'fetchTaskLibraryBootstrap',
      'fetchCustomColumns',
      'openEditTaskTemplateDialog',
      'closeAddTaskTemplateDialog',
      'createTaskTemplate',
      'updateTaskTemplate',
    ]),

    ...mapMutations('setup', ['setDetailTab', 'setCycleType']),

    async loadPage(id) {
      if (!id) return;
      this.setDetailTab('assignments');

      try {
        const template = await this.fetchTaskTemplateDetail(id);
        if (template) {
          this.setCycleType(template.cycle_type || template.cycleType || 'FBC');
        }

        const needsBootstrap =
          !this.locations?.length || !this.users?.length || !this.chartOfAccounts?.length;

        if (needsBootstrap) {
          await this.fetchTaskLibraryBootstrap();
        } else if (!this.customColumns.length) {
          await this.fetchCustomColumns();
        }
      } catch (err) {
        console.error('[SetupTaskDetail]: load page error', err);
      }
    },

    goBack() {
      // Preserve cid — bare router.push drops query and can leave FC.
      const cid = this.$route.query.cid || this.$store.state.client;
      const query = { ...(this.$route.query || {}) };
      if (cid) query.cid = String(cid);
      this.$router
        .push({ name: 'fc-setup-tasklist', query })
        .catch((err) => {
          if (err?.name === 'NavigationDuplicated') return;
          this.$router.push({ path: FC_ROUTES.SETUP_TASKLIST, query }).catch((fallbackErr) => {
            if (fallbackErr?.name === 'NavigationDuplicated') return;
            console.error('[SetupTaskDetail]: navigate back failed', fallbackErr || err);
          });
        });
    },

    onDetailTabChange(tab) {
      this.setDetailTab(tab);
    },

    handleEditTemplate() {
      if (!this.templateId) return;
      this.openEditTaskTemplateDialog({
        templateId: this.templateId,
        taskType: this.activeTemplate?.task_type,
      });
    },

    async handleSaveTemplate({ id, data, mode }) {
      this.templateDialogLoading = true;
      try {
        if (mode === 'edit' && id) {
          await this.updateTaskTemplate({ id, data });
        } else {
          await this.createTaskTemplate(data);
        }
        this.closeAddTaskTemplateDialog();
        await this.fetchTaskTemplateDetail(this.templateId);
      } catch (err) {
        const fieldErrors = readTemplateSaveFieldErrors(err);
        if (fieldErrors) {
          this.$refs.addTemplateDialog?.applyServerFieldErrors?.(fieldErrors);
        }
        console.error('[SetupTaskDetail]: save template error', err);
      } finally {
        this.templateDialogLoading = false;
      }
    },
  },
};
</script>
